"use client";

import { EmWarningIcon } from "@/components/icons-lib";
import type { IFindMySchoolSchoolItem } from "@/providers/store/api/find-my-school/find-my-school.interface";

import { SchoolListItem, type SchoolListItemProps } from "./school-list-item";


export interface SchoolListProps {
  schools: IFindMySchoolSchoolItem[];
  highlightedSchoolId?: string | null;
  onSchoolClick?: SchoolListItemProps["onClick"];
  isLoading?: boolean;
  outsideDistrictBoundary?: boolean;
  hasSearchedAddress?: boolean;
  variant?: SchoolListItemProps["variant"];
}

export function SchoolList({
  schools,
  highlightedSchoolId,
  onSchoolClick,
  isLoading = false,
  outsideDistrictBoundary = false,
  hasSearchedAddress = false,
  variant = "desktop",
}: SchoolListProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-lg border border-gray-200 bg-gray-100"
          />
        ))}
      </div>
    );
  }

  if (outsideDistrictBoundary && hasSearchedAddress) {
    return (
      <div className="flex gap-3 rounded-[12px] border border-[#DFDFDF] bg-[#FFF8F0] p-4">
        <EmWarningIcon className="h-8 w-8 shrink-0 text-[#520313]" />
        <p className="text-left text-[14px]/[20px] font-normal text-[#161515]">
          We are sorry. It appears that your address falls outside of the school attendance boundaries for
          this district. To find out if your children are eligible to attend schools in this district please
          contact the administration offices.
        </p>
      </div>
    );
  }

  if (schools.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-gray-500">
          {hasSearchedAddress
            ? "No school assignment was found for this location."
            : "No schools are available for this district."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {schools.map((school) => (
        <SchoolListItem
          key={school.id}
          isHighlighted={school.id === highlightedSchoolId}
          school={school}
          variant={variant}
          onClick={onSchoolClick}
        />
      ))}
    </div>
  );
}
